import { Flex, Spin, Empty, Card, Pagination, Modal, Input } from 'antd';
import styles from './redirection-targets.module.scss';
import { CompanySelectBh } from '../company-select-bh/company-select-bh';
import { useSelectedCompany } from '@/hooks/use-selected-company/use-selected-company';
import { Button } from '../button/button';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import Search from '@/assets/icons/search-md.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import EditIcon from '@/assets/icons/pencil-solid-full.svg?react';
import TrashIcon from '@/assets/icons/trash-01.svg?react';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import { useState, useMemo } from 'react';
import { debounce } from '@/utils/debounce/debounce';

const SEARCH_DELAY = 500;
import {
  useRedirectionTargets,
  useCreateRedirectionTarget,
  useUpdateRedirectionTarget,
  useDeleteRedirectionTarget,
} from '@/hooks/use-redirection-targets/use-redirection-targets';
import {
  RedirectionTargetModal,
  RedirectionTargetFormValues,
} from '../redirection-target-modal/redirection-target-modal';
import { RedirectionTarget } from '@/services/redirection-targets-service/redirection-targets-service.types';

export function RedirectionTargets() {
  const { selectedCompany, setSelectedCompany } = useSelectedCompany({
    storageKey: 'selectedCompany_redirectionTargets',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedTarget, setSelectedTarget] = useState<RedirectionTarget | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState<number | null>(null);
  const [filterValue, setFilterValue] = useState('');

  const { redirectionTargets, totalCount, isLoading } = useRedirectionTargets(
    selectedCompany?.id
      ? {
        company_id: selectedCompany.id,
        page: currentPage,
        items_per_page: pageSize,
        search: filterValue || undefined,
      }
      : undefined
  );

  const { createRedirectionTarget, isPending: isCreating } = useCreateRedirectionTarget();
  const { updateRedirectionTarget, isPending: isUpdating } = useUpdateRedirectionTarget();
  const { deleteRedirectionTarget, isPending: isDeleting } = useDeleteRedirectionTarget();

  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      setFilterValue(value);
      setCurrentPage(1);
    }, SEARCH_DELAY),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedTarget(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (target: RedirectionTarget) => {
    setModalMode('edit');
    setSelectedTarget(target);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTarget(null);
  };

  const handleSave = (values: RedirectionTargetFormValues) => {
    if (!selectedCompany) return;

    if (modalMode === 'create') {
      createRedirectionTarget(
        {
          companyId: selectedCompany.id,
          payload: {
            redirection_target: {
              name: values.name,
              phone: values.phone,
              email: values.email,
              extension: values.extension,
            },
          },
        },
        {
          onSuccess: () => {
            handleCloseModal();
          },
        }
      );
    } else if (modalMode === 'edit' && selectedTarget) {
      updateRedirectionTarget(
        {
          id: selectedTarget.id,
          companyId: selectedCompany.id,
          payload: {
            redirection_target: {
              name: values.name,
              phone: values.phone,
              email: values.email,
              extension: values.extension,
            },
          },
        },
        {
          onSuccess: () => {
            handleCloseModal();
          },
        }
      );
    }
  };

  const handleOpenDeleteModal = (id: number) => {
    setTargetToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setTargetToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (targetToDelete && selectedCompany) {
      deleteRedirectionTarget(
        { id: targetToDelete, companyId: selectedCompany.id },
        {
          onSuccess: () => {
            handleCloseDeleteModal();

            const remainingItems = totalCount - 1;
            const maxPage = Math.ceil(remainingItems / pageSize);

            if (currentPage > maxPage && maxPage > 0) {
              setCurrentPage(maxPage);
            } else if (remainingItems === 0) {
              setCurrentPage(1);
            }
          },
        }
      );
    }
  };

  return (
    <Flex vertical gap={16}>
      <CompanySelectBh value={selectedCompany} onChange={setSelectedCompany} />

      {selectedCompany && (
        <>
          <Button
            buttonVariant="primary"
            onClick={handleOpenCreateModal}
            className={styles.addButton}
          >
            <Icon component={() => <PlusIcon className={styles.plusIcon} />} />
            Add New Target
          </Button>

          <Flex justify="space-between" align="center" gap={16} wrap="wrap" style={{ marginBottom: 16 }}>
            <Flex align="center" gap={8}>
              <span className={styles.targetCountLabel}>Target Count:</span>
              <span className={styles.targetCountValue}>{totalCount}</span>
            </Flex>
            <Flex vertical gap={5} className={styles.searchContainer}>
              <Input
                onChange={handleSearchChange}
                placeholder="Search"
                prefix={
                  <Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />
                }
              />
            </Flex>
          </Flex>

          {isLoading ? (
            <div className={styles.loadingWrapper}>
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          ) : redirectionTargets.length === 0 ? (
            <Empty description="No redirection targets found" />
          ) : (
            <>
              <Flex vertical gap={12}>
                {redirectionTargets.map((target) => (
                  <Card key={target.id} className={styles.targetCard}>
                    <Flex justify="space-between" align="flex-start">
                      <Flex vertical gap={8} flex={1}>
                        <h3 className={styles.targetName}>{target.name}</h3>
                        <Flex vertical gap={4}>
                          <p className={styles.targetInfo}>
                            <span className={styles.label}>Phone:</span> {target.phone}
                          </p>
                          {target.email && (
                            <p className={styles.targetInfo}>
                              <span className={styles.label}>Email:</span> {target.email}
                            </p>
                          )}
                          {target.extension && (
                            <p className={styles.targetInfo}>
                              <span className={styles.label}>Extension:</span> {target.extension}
                            </p>
                          )}
                        </Flex>
                      </Flex>
                      <Flex gap={8}>
                        <Button
                          buttonVariant="secondary"
                          onClick={() => handleOpenEditModal(target)}
                          className={styles.actionButton}
                        >
                          <Icon component={() => <EditIcon className={styles.editIcon} />} />
                        </Button>
                        <Button
                          buttonVariant="secondary"
                          onClick={() => handleOpenDeleteModal(target.id)}
                          loading={isDeleting && targetToDelete === target.id}
                          className={styles.actionButton}
                        >
                          <Icon component={() => <TrashIcon className={styles.trashIcon} />} />
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                ))}
              </Flex>

              {totalCount > 10 && (
                <Flex justify="center" style={{ marginTop: 24 }}>
                  <Pagination
                    className={styles.pagination}
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalCount}
                    onChange={(page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    }}
                    pageSizeOptions={['10', '25', '50']}
                    showSizeChanger
                    locale={{ items_per_page: '' }}
                    nextIcon={<Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />}
                    prevIcon={<Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />}
                  />
                </Flex>
              )}
            </>
          )}

          <RedirectionTargetModal
            open={modalOpen}
            onClose={handleCloseModal}
            onSave={handleSave}
            isLoading={isCreating || isUpdating}
            initialValues={selectedTarget}
            mode={modalMode}
          />

          <Modal
            title="Delete Redirection Target"
            open={deleteModalOpen}
            onCancel={handleCloseDeleteModal}
            onOk={handleConfirmDelete}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: isDeleting }}
          >
            <p>Are you sure you want to delete this redirection target? This action cannot be undone.</p>
          </Modal>
        </>
      )}
    </Flex>
  );
}
